import type { ReactNode } from "react";
import { MicroLabel } from "./MicroLabel";

/**
 * The caption over a form field, with the "(optional)" or "(required)" half
 * held in one place: optional muted, required in red.
 *
 * It lived under `v2/novelty/positions/` because that is where it was first
 * needed, and two screens there wrote it four times between them. The optional
 * half was the part that drifted: the same idea had been spelled as a label
 * and as a paragraph with a nested span in each.
 *
 * A caption with no control to name is a paragraph, not a label: a `for` that
 * points nowhere is worse for a screen reader than no label element at all.
 */
export function FieldLabel({
  htmlFor,
  optional = false,
  required = false,
  look = "caption",
  children,
}: {
  htmlFor?: string;
  optional?: boolean;
  /** The field must be answered before the form's act opens. */
  required?: boolean;
  /** The section caption, or the house modal's sentence-case label over its field. */
  look?: "caption" | "sentence";
  children: ReactNode;
}) {
  if (look === "sentence") {
    return (
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-[12px] font-semibold text-foreground"
      >
        {children}
        {optional && (
          <>
            {" "}
            <span className="font-normal text-subtle-foreground">
              (optional)
            </span>
          </>
        )}
        {required && (
          <>
            {" "}
            <span className="font-normal text-destructive">(required)</span>
          </>
        )}
      </label>
    );
  }
  const words = (
    <>
      {children}
      {optional && (
        <>
          {" "}
          <span className="font-medium normal-case tracking-normal">
            (optional)
          </span>
        </>
      )}
      {required && (
        <>
          {" "}
          <span className="font-medium normal-case tracking-normal text-destructive">
            (required)
          </span>
        </>
      )}
    </>
  );
  return htmlFor === undefined ? (
    <MicroLabel as="p" size="sm" className="mb-1 block">
      {words}
    </MicroLabel>
  ) : (
    <MicroLabel as="label" size="sm" htmlFor={htmlFor} className="mb-1 block">
      {words}
    </MicroLabel>
  );
}
